// Libraries
import PropTypes from 'prop-types';
import React from 'react';
import styled, { keyframes } from 'styled-components';

const ripple = keyframes`
    0% {
        opacity: 1;
        transform: scale(0, 0);
    }

    25% {
        opacity: 0.75;
        transform: scale(25, 25);
    }

    50% {
        opacity: 0.5;
        transform: scale(50, 50);
    }

    75% {
        opacity: 0.25;
        transform: scale(75, 75);
    }

    100% {
        opacity: 0;
        transform: scale(100, 100);
    }
`;

const ButtonWrapper = styled.button`
    background-color: #ff7744;
    border-radius: 3px;
    color: #fff;
    cursor: pointer;
    font-size: 14px;
    opacity: ${props => (props.disabled ? '0.5' : '1')};
    overflow: hidden;
    pointer-events: ${props => (props.disabled ? 'none' : 'all')};
    position: relative;
    width: ${props => props.width};
    transition: 0.3s;

    &::after {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 100%;
        content: '';
        height: 100%;
        left: 50%;
        opacity: 0;
        position: absolute;
        width: 5%;
    }

    &:focus {
        &:not(:active) {
            &::after {
                animation: ${ripple} 0.5s ease-out;
            }
        }
    }
`;

const PrimaryButtonWrapper = styled(ButtonWrapper)`
    background: ${props => (props.disabled ? '#bfbdbe' : '#ff7744')};
    border: none;
    border-radius: 4px;
    font-family: 'lato-regular';
    font-size: 14px;
    font-weight: 500;
    height: 45px;
    border: '2px solid #ff7744';
    &:hover {
        background-color: #ff5500; /* Change this to your desired hover color */
    }
`;

const SecondaryButtonWrapper = styled(ButtonWrapper)`
    background-color: #fff;
    border: 1px solid #ff7744;
    color: #ff7744;
    height: 45px;

    &:hover {
        background-color: #f6f6f6;
    }
`;

const DraftButtonWrapper = styled(ButtonWrapper)`
    border: 1px solid #cecece;
    height: 45px;
    font-weight: 500;
    color: #ababab;
    background-color: white;

    &:hover {
        background-color: #f6f6f6;
    }
`;

const RoundedButtonWrapper = styled(ButtonWrapper)`
    background: #ff7744;
    border: none;
    border-radius: 50%;
    height: 52px;
    position: relative;
    width: 52px;

    &::before {
        content: '→';
        font-size: 25px;
        left: 25%;
        position: absolute;
        top: 10%;
    }
`;

const Button = props => {
    const {
        customClass,
        text,
        handleClick,
        buttonType,
        width,
        type,
        isDisable,
        title,
        id,
    } = props;

    function handleButtonClick(values) {
        handleClick && handleClick(values);
    }

    return (
        <React.Fragment>
            {buttonType === 'primary' && (
                <PrimaryButtonWrapper
                    onClick={handleButtonClick}
                    className={`${customClass && customClass} ${
                        isDisable ? 'disabled' : ''
                    }`}
                    id={id}
                    data-testid={id}
                    width={width || '100%'}
                    type={type || ''}
                    disabled={isDisable || ''}
                    title={title}
                >
                    {text}
                </PrimaryButtonWrapper>
            )}
            {buttonType === 'secondary' && (
                <SecondaryButtonWrapper
                    onClick={handleButtonClick}
                    className={`${customClass && customClass} ${
                        isDisable ? 'disabled' : ''
                    }`}
                    id={id}
                    data-testid={id}
                    width={width || '100%'}
                    type={type || ''}
                    disabled={isDisable || ''}
                    title={title}
                >
                    {text}
                </SecondaryButtonWrapper>
            )}
            {buttonType === 'rounded' && (
                <RoundedButtonWrapper
                    onClick={handleButtonClick}
                    className={`${customClass && customClass} ${
                        isDisable ? 'disabled' : ''
                    }`}
                    id={id}
                    data-testid={id}
                    width={width || '100%'}
                    type={type || ''}
                    disabled={isDisable || ''}
                    title={title}
                >
                    {text}
                </RoundedButtonWrapper>
            )}
            {buttonType === 'draft' && (
                <DraftButtonWrapper
                    onClick={handleButtonClick}
                    className={`${customClass && customClass} ${
                        isDisable ? 'disabled' : ''
                    }`}
                    id={id}
                    data-testid={id}
                    width={width || '100%'}
                    type={type || ''}
                    disabled={isDisable || ''}
                    title={title}
                >
                    {text}
                </DraftButtonWrapper>
            )}
        </React.Fragment>
    );
};

Button.propTypes = {
    buttonType: PropTypes.string,
    customClass: PropTypes.string,
    handleClick: PropTypes.func,
    text: PropTypes.string,
    type: PropTypes.string,
    width: PropTypes.string,
    isDisable: PropTypes.bool,
    title: PropTypes.string,
    id: PropTypes.string,
};

Button.defaultProps = {
    type: 'submit',
};

export default Button;
